import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ApiService } from 'app/services/api.service';
import { AnimationCollapse, AnimationCollapseLeave } from 'app/animations';
import { DeliveryClassPipe, MomentPipe } from 'app/pipes/pipes';
import { HelperService } from 'app/services/helper.service';
import { Areas } from 'app/models/permission.model';
import { CanPipe } from 'app/pipes/can.pipe';

@Component({
    selector: 'app-active-alerts',
    imports: [CommonModule, TranslateModule, DeliveryClassPipe, MomentPipe, CanPipe],
    templateUrl: './active-alerts.component.html',
    styleUrls: ['./active-alerts.component.css'],
    changeDetection: ChangeDetectionStrategy.Default,
    animations: [AnimationCollapse, AnimationCollapseLeave]
})
export class ActiveAlertsComponent {

  constructor(private api: ApiService, private helper: HelperService) { }

  readonly Areas = Areas;

  @Input() showCustomer: boolean = false;
  /** Overrides the cancel permission; the landing page has no permission map */
  @Input() cancelAllowed: boolean | null = null;

  user: object = JSON.parse(localStorage.getItem('user'));

  panelOpen: boolean;
  activeAlerts: any[];
  activeAlertsLoaded: boolean = false;
  /** The alerts could not be loaded: shown as an error, never as "no alarms" */
  activeAlertsLoadError: boolean = false;
  expandedAlertId: string | null = null;

  /**
   * Load all active alerts for the customer
   */
  loadActiveAlerts(): void {
    this.activeAlertsLoaded = false;
    // Check that active alerts panel has been opened from the UI
    if (!this.panelOpen) {
      this.fetchActiveAlerts();
    } else {
      // If panel was closed clear the alerts from the memory
      this.activeAlerts = null;
      this.activeAlertsLoadError = false;
    }
    this.panelOpen = !this.panelOpen;
  }

  /**
   * Load the alerts again after a failed load, keeping the panel open
   */
  retryActiveAlerts(): void {
    this.activeAlertsLoaded = false;
    this.fetchActiveAlerts();
  }

  private fetchActiveAlerts(): void {
    this.activeAlertsLoadError = false;
    this.api.getActiveAlerts()
      .subscribe({
        next: res => {
          this.activeAlertsLoaded = true;
          this.activeAlerts = res ? res : null;
        },
        // A failed load (an outage, a refused request) must not look like an all-clear
        error: () => {
          this.activeAlertsLoaded = true;
          this.activeAlerts = null;
          this.activeAlertsLoadError = true;
        }
      });
  }

  /**
   * Toggle the expanded detail row for a given alert
   * @param taskId the task ID of the alert row to expand/collapse
   */
  toggleExpand(taskId: string): void {
    this.expandedAlertId = this.expandedAlertId === taskId ? null : taskId;
  }

  /**
   * Cancel an active alert
   * @param alert The alert to cancel
   */
  cancelAlert(alert: any): void {
    this.api.cancelActiveAlert(alert.taskId)
      .subscribe(_ => {
        alert.cancelled = true;
      },
        err => {
          alert.error = err;
        })
  }

  /**
   * Get localized activation name based on group, node and device type
   * @param group activation group
   * @param node activation node
   * @param type activation type
   * @param customAct custom activation name
   * @param connTimeLimit connection time limit
   * @returns the localized activation name
   */
  getActivationName(group: number, node: number, type: string, customAct: string, connTimeLimit: number): string {
    return this.helper.getActivationName(group, node, type, customAct, connTimeLimit);
  }
}
